// module imports
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';

interface UploadState {
  data: {
    [key: string]: {
      id: string;
      fileName: string;
      url: string;
      progress: number;
      status: 'uploading' | 'completed' | 'error';
      error?: string;
      uploadedAt?: string;
    };
  };
  loading: boolean;
  error: string | null;
}

interface UploadFileParams {
  file: File;
  folder?: string;
  makePublic?: boolean;
  customFileName?: string;
}

interface UploadImageParams {
  file: File;
  folder?: string;
  makePublic?: boolean;
  customFileName?: string;
}

export const uploadFile = createAsyncThunk('upload/uploadFile', async (params: UploadFileParams, { rejectWithValue }) => {
  try {
    const { file, folder = 'uploads', makePublic = true, customFileName } = params;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    formData.append('makePublic', makePublic.toString());
    if (customFileName) {
      formData.append('fileName', customFileName);
    }

    const response = await axiosInstance.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (response.data.success) {
      return {
        id: Date.now().toString(),
        fileName: response.data.data.fileName,
        url: response.data.data.url,
        progress: 100,
        status: 'completed' as const,
        uploadedAt: new Date().toISOString(),
      };
    } else {
      throw new Error(response.data.error || 'Upload failed');
    }
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || error.message || 'Upload failed');
  }
});

export const uploadImage = createAsyncThunk('upload/uploadImage', async (params: UploadImageParams, { rejectWithValue }) => {
  try {
    const { file, folder = 'uploads', makePublic = true, customFileName } = params;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    formData.append('makePublic', makePublic.toString());
    if (customFileName) {
      formData.append('fileName', customFileName);
    }

    const response = await axiosInstance.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (response.data.success) {
      return {
        id: Date.now().toString(),
        fileName: response.data.data.fileName,
        url: response.data.data.url,
        progress: 100,
        status: 'completed' as const,
        uploadedAt: new Date().toISOString(),
      };
    } else {
      throw new Error(response.data.error || 'Upload failed');
    }
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || error.message || 'Upload failed');
  }
});

export const uploadProfilePhoto = createAsyncThunk('upload/uploadProfilePhoto', async (file: File, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post('/upload/profile-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (response.data.success) {
      return {
        id: 'profile-photo',
        fileName: response.data.data.fileName,
        url: response.data.data.url,
        progress: 100,
        status: 'completed' as const,
        uploadedAt: new Date().toISOString(),
      };
    } else {
      throw new Error(response.data.error || 'Profile photo upload failed');
    }
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || error.message || 'Profile photo upload failed');
  }
});

export const deleteUploadedFile = createAsyncThunk('upload/deleteFile', async (fileName: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete('/upload', { data: { fileName } });
    if (response.data.success) {
      return fileName;
    } else {
      throw new Error(response.data.error || 'Delete failed');
    }
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || error.message || 'Delete failed');
  }
});

const initialState: UploadState = {
  data: {},
  loading: false,
  error: null,
};

// Slice
const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    clearUploads: (state) => {
      state.data = {};
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    removeUpload: (state, action: PayloadAction<string>) => {
      delete state.data[action.payload];
    },
    updateUploadProgress: (state, action: PayloadAction<{ id: string; progress: number }>) => {
      const { id, progress } = action.payload;
      if (state.data[id]) {
        state.data[id].progress = progress;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload file
      .addCase(uploadFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.loading = false;
        state.data[action.payload.id] = action.payload;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Upload image
      .addCase(uploadImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadImage.fulfilled, (state, action) => {
        state.loading = false;
        state.data[action.payload.id] = action.payload;
      })
      .addCase(uploadImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Upload profile photo
      .addCase(uploadProfilePhoto.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadProfilePhoto.fulfilled, (state, action) => {
        state.loading = false;
        state.data[action.payload.id] = action.payload;
      })
      .addCase(uploadProfilePhoto.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete file
      .addCase(deleteUploadedFile.fulfilled, (state, action) => {
        // Remove from data if it exists
        Object.keys(state.data).forEach((id) => {
          if (state.data[id].fileName === action.payload) {
            delete state.data[id];
          }
        });
      })
      .addCase(deleteUploadedFile.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearUploads, clearError, removeUpload, updateUploadProgress } = uploadSlice.actions;
export default uploadSlice.reducer;
